import React from 'react';
import { stringToColor } from '../lib/avatarColor';
import PresenceDot from './PresenceDot';
import { RT3PresenceDot } from './RT3PresenceDot';
import { usePresence } from '../hooks/usePresence';
import { usePresenceStore } from '../stores';
import type { AgentRuntimeState, AgentRuntimeReason } from '../lib/api';

interface UserAvatarProps {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  size?: number;
  onClick?: () => void;
  role?: string;
  state?: AgentRuntimeState;
  reason?: AgentRuntimeReason;
  /** The owner switched this agent off. Greys the portrait and outranks live presence. */
  disabled?: boolean;
  showPresence?: boolean;
}

export function UserAvatar({ userId, displayName, avatarUrl, size = 36, onClick, role, state: stateProp, reason: reasonProp, disabled = false, showPresence = true }: UserAvatarProps) {
  const fontSize = Math.round(size * 0.42);
  const isAgent = role === 'agent';
  const needsPresence = isAgent && showPresence;
  const live = usePresence(needsPresence ? userId : undefined);
  const wsOnline = usePresenceStore(needsPresence ? (s => !!s.onlineUserIds[userId]) : () => false);
  const state = live?.state ?? stateProp ?? (wsOnline ? 'online' : undefined);
  const reason = live?.reason ?? (state === 'error' ? reasonProp : undefined);
  const hasDot = showPresence && (role != null);
  // A live presence frame can still arrive for an agent the owner just
  // switched off, so the flag wins over `state` rather than merging with it.
  const avatarClass = disabled ? 'agent-avatar-disabled' : undefined;

  const avatarEl = avatarUrl ? (
    <img
      src={avatarUrl}
      alt={displayName}
      className={avatarClass}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', cursor: onClick ? 'pointer' : undefined }}
      onClick={onClick}
    />
  ) : (
    <div
      className={avatarClass}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize,
        fontWeight: 700,
        color: '#fff',
        background: stringToColor(userId),
        flexShrink: 0,
        cursor: onClick ? 'pointer' : undefined,
      }}
      onClick={onClick}
    >
      {displayName[0]?.toUpperCase()}
    </div>
  );

  if (!hasDot) return avatarEl;

  return (
    <div style={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}>
      {avatarEl}
      <div style={{ position: 'absolute', bottom: -2, right: -2 }}>
        {isAgent ? <PresenceDot state={state} reason={reason} disabled={disabled} compact /> : <RT3PresenceDot userID={userId} />}
      </div>
    </div>
  );
}
